import { humanizeError } from "@/lib/errors";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Eye, EyeOff, MoreHorizontal, Plus, Send, ShieldAlert, Trash2 } from "lucide-react";

import {
  APIError,
  deleteChannel,
  listAccessTokens,
  listChannels,
  listDecisions,
  resolveDecision,
  answerDecision,
  dismissDecision,
  testChannel,
  updateChannel,
  type AccessToken,
  type Channel,
  type PendingDecision,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/components/ui/toast";
import { useConfirm } from "@/components/ui/confirm";
import { Spinner } from "@/components/Page";

function errMsg(e: unknown, fallback: string) {
  return humanizeError(e, fallback);
}
const is403 = (e: unknown) => e instanceof APIError && e.status === 403;

function AdminOnly({ what }: { what: string }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-md border border-dashed py-10 text-center text-sm text-muted-foreground">
      <ShieldAlert className="h-6 w-6 opacity-50" />
      <span>
        You need <span className="font-medium text-foreground">admin</span> access on this project to {what}.
      </span>
    </div>
  );
}

const KIND_LABEL: Record<string, string> = { telegram: "Telegram", slack: "Slack" };

function prettyJSON(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

// expiresLabel turns a ticket's expires_at into a short "expires in 6d / 45m"
// (or "expired"), so the operator sees how long they have to act.
function expiresLabel(iso?: string): string {
  if (!iso) return "";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `expires in ${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `expires in ${hrs}h`;
  return `expires in ${Math.round(hrs / 24)}d`;
}

export function ProjectNotifications({ projectId }: { projectId: string }) {
  return (
    <div className="space-y-6">
      <PendingApprovalsCard projectId={projectId} />
      <ChannelsCard projectId={projectId} />
    </div>
  );
}

// PendingApprovalsCard lists HOLD calls awaiting a human decision and lets an
// admin approve/reject them in-app. Polls so a blocked agent's request
// surfaces; an approval clears it within the agent's wait window. (The Telegram
// card links to a dedicated /approve page instead — this is the in-dashboard
// queue.)
function PendingApprovalsCard({ projectId }: { projectId: string }) {
  const [items, setItems] = useState<PendingDecision[]>([]);
  const [tokens, setTokens] = useState<AccessToken[]>([]);
  const [canResolve, setCanResolve] = useState(false);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({}); // ticket → answer draft
  const toast = useToast();

  const principalName = (id?: string) => (!id ? "Any caller" : tokens.find((t) => t.id === id)?.name ?? id);

  const refresh = useCallback(async () => {
    try {
      const [r, toks] = await Promise.all([
        listDecisions(projectId),
        listAccessTokens(projectId).catch(() => [] as AccessToken[]),
      ]);
      setItems(r.decisions);
      setCanResolve(r.canResolve);
      setTokens(toks);
    } catch {
      // best-effort; the panel shows what it last had
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void refresh();
    const t = setInterval(() => void refresh(), 10000);
    return () => clearInterval(t);
  }, [refresh]);

  const onResolve = useCallback(
    async (ticket: string, approve: boolean) => {
      setActing(ticket);
      try {
        await resolveDecision(projectId, ticket, approve);
        setItems((cur) => cur.filter((d) => d.ticket_id !== ticket));
        toast.success(approve ? "Approved." : "Rejected.");
      } catch (e) {
        toast.error(is403(e) ? "You need admin access to resolve approvals." : errMsg(e, "Couldn't resolve"));
        void refresh();
      } finally {
        setActing(null);
      }
    },
    [projectId, toast, refresh],
  );

  const onDismiss = useCallback(
    async (ticket: string) => {
      setActing(ticket);
      try {
        await dismissDecision(projectId, ticket);
        setItems((cur) => cur.filter((d) => d.ticket_id !== ticket));
        toast.success("Dismissed.");
      } catch (e) {
        toast.error(is403(e) ? "You need admin access to dismiss." : errMsg(e, "Couldn't dismiss"));
        void refresh();
      } finally {
        setActing(null);
      }
    },
    [projectId, toast, refresh],
  );

  const onAnswer = useCallback(
    async (ticket: string) => {
      const answer = (drafts[ticket] ?? "").trim();
      if (!answer) return;
      setActing(ticket);
      try {
        await answerDecision(projectId, ticket, answer);
        setItems((cur) => cur.filter((d) => d.ticket_id !== ticket));
        setDrafts((cur) => {
          const next = { ...cur };
          delete next[ticket];
          return next;
        });
        toast.success("Answer sent to the agent.");
      } catch (e) {
        toast.error(is403(e) ? "You need admin access to answer." : errMsg(e, "Couldn't send the answer"));
        void refresh();
      } finally {
        setActing(null);
      }
    },
    [drafts, projectId, toast, refresh],
  );

  if (!loading && items.length === 0) {
    return null; // nothing pending → don't clutter the tab
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Pending approvals
          {items.length > 0 && (
            <span className="ml-2 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-warning/20 px-1.5 text-xs font-semibold text-warning">
              {items.length}
            </span>
          )}
        </CardTitle>
        <CardDescription>
          HOLD calls waiting on a decision, and questions the agent escalated for you to answer.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="divide-y rounded-md border">
          {items.map((d) =>
            d.kind === "question" ? (
              <li key={d.ticket_id} className="space-y-2 px-4 py-3">
                <div className="text-xs text-muted-foreground">
                  Question from {principalName(d.principal)} · {new Date(d.created_at).toLocaleString()}
                  {expiresLabel(d.expires_at) ? ` · ${expiresLabel(d.expires_at)}` : ""}
                  {d.notified_channels?.length ? ` · sent to ${d.notified_channels.join(", ")}` : ""}
                </div>
                <div className="whitespace-pre-wrap text-sm">{d.question || d.reason}</div>
                {canResolve ? (
                  <div className="space-y-2">
                    <textarea
                      value={drafts[d.ticket_id] ?? ""}
                      onChange={(e) => setDrafts((cur) => ({ ...cur, [d.ticket_id]: e.target.value }))}
                      placeholder="Type the answer to teach the agent…"
                      rows={3}
                      className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={acting === d.ticket_id}
                        onClick={() => void onDismiss(d.ticket_id)}
                        title="Clear this question without answering"
                      >
                        Dismiss
                      </Button>
                      <Button
                        size="sm"
                        disabled={acting === d.ticket_id || !(drafts[d.ticket_id] ?? "").trim()}
                        onClick={() => void onAnswer(d.ticket_id)}
                      >
                        {acting === d.ticket_id && <Spinner />} Send answer
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">An admin can answer this.</div>
                )}
              </li>
            ) : (
              <li key={d.ticket_id} className="flex items-start justify-between gap-3 px-4 py-3">
                <div className="min-w-0 space-y-1">
                  <div className="truncate font-mono text-sm">{d.tool}</div>
                  <div className="text-xs text-muted-foreground">
                    {principalName(d.principal)} · {new Date(d.created_at).toLocaleString()}
                    {d.reason ? ` · ${d.reason}` : ""}
                    {expiresLabel(d.expires_at) ? ` · ${expiresLabel(d.expires_at)}` : ""}
                    {d.notified_channels?.length ? ` · sent to ${d.notified_channels.join(", ")}` : ""}
                  </div>
                  {d.args_preview && (
                    <pre className="mt-1 max-h-40 overflow-auto rounded border bg-muted/30 p-2 text-[11px]">
                      {prettyJSON(d.args_preview)}
                    </pre>
                  )}
                </div>
                {canResolve && (
                  <div className="flex shrink-0 items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={acting === d.ticket_id}
                      onClick={() => void onDismiss(d.ticket_id)}
                      title="Clear without approving or rejecting"
                    >
                      Dismiss
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={acting === d.ticket_id}
                      onClick={() => void onResolve(d.ticket_id, false)}
                      className="text-destructive focus:text-destructive"
                    >
                      Reject
                    </Button>
                    <Button size="sm" disabled={acting === d.ticket_id} onClick={() => void onResolve(d.ticket_id, true)}>
                      {acting === d.ticket_id && <Spinner />} Approve
                    </Button>
                  </div>
                )}
              </li>
            ),
          )}
        </ul>
      </CardContent>
    </Card>
  );
}

function ChannelsCard({ projectId }: { projectId: string }) {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const goCreate = () => navigate(`/agents/${projectId}/channels/new`);

  const refresh = useCallback(async () => {
    try {
      const { channels } = await listChannels(projectId);
      setChannels(channels);
      setErr(null);
      setForbidden(false);
    } catch (e) {
      if (is403(e)) setForbidden(true);
      else setErr(errMsg(e, "Couldn't load channels"));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onTest = useCallback(
    async (c: Channel) => {
      setBusy(c.id);
      try {
        await testChannel(projectId, c.id);
        toast.success(`Test sent to "${c.name}". Check the channel.`);
      } catch (e) {
        toast.error(errMsg(e, "Test delivery failed"));
      } finally {
        setBusy(null);
      }
    },
    [projectId, toast],
  );

  const onToggle = useCallback(
    async (c: Channel) => {
      setBusy(c.id);
      try {
        await updateChannel(projectId, c.id, { name: c.name, kind: c.kind, enabled: !c.enabled });
        setChannels((cur) => cur.map((x) => (x.id === c.id ? { ...x, enabled: !x.enabled } : x)));
      } catch (e) {
        toast.error(errMsg(e, "Update failed"));
      } finally {
        setBusy(null);
      }
    },
    [projectId, toast],
  );

  const onDelete = useCallback(
    async (c: Channel) => {
      const ok = await confirm({
        title: `Delete "${c.name}"?`,
        description: "Approvals and messages will stop going to this channel. This can't be undone.",
        confirmText: "Delete",
        cancelText: "Cancel",
      });
      if (!ok) return;
      try {
        await deleteChannel(projectId, c.id);
        setChannels((cur) => cur.filter((x) => x.id !== c.id));
        toast.success(`"${c.name}" deleted.`);
      } catch (e) {
        toast.error(errMsg(e, "Delete failed"));
      }
    },
    [projectId, confirm, toast],
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base">Channels</CardTitle>
          <CardDescription>
            Where approval requests are sent when a policy rule holds a tool call for review. With no channels, the
            workspace default is used. Approvals are always resolvable in the Policy tab.
          </CardDescription>
        </div>
        {!forbidden && (
          <Button size="sm" onClick={goCreate}>
            <Plus className="h-4 w-4" /> New channel
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Spinner /> Loading…
          </div>
        ) : forbidden ? (
          <AdminOnly what="manage channels" />
        ) : err ? (
          <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
            <span className="text-destructive">{err}</span>
            <Button variant="outline" size="sm" onClick={() => void refresh()}>
              Retry
            </Button>
          </div>
        ) : channels.length === 0 ? (
          <button
            onClick={goCreate}
            className="flex w-full flex-col items-center gap-2 rounded-md border border-dashed py-10 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-muted/30"
          >
            <Bell className="h-6 w-6 opacity-50" />
            <span>
              No channels yet. <span className="font-medium text-foreground">Add one</span> to get approval requests
              in Telegram.
            </span>
          </button>
        ) : (
          <ul className="divide-y rounded-md border">
            {channels.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="flex min-w-0 items-center gap-3">
                  <Bell className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium">{c.name}</span>
                      {c.role === "agent" && (
                        <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary">
                          agent chat
                        </Badge>
                      )}
                      {!c.enabled && (
                        <Badge variant="outline" className="border-border bg-muted text-muted-foreground">
                          disabled
                        </Badge>
                      )}
                    </div>
                    <div className="font-mono text-xs text-muted-foreground">
                      {KIND_LABEL[c.kind] ?? c.kind}&nbsp;·&nbsp;created {new Date(c.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {busy === c.id && <Spinner />}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" aria-label="Channel actions">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => void onTest(c)}>
                        <Send /> Send test
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => void onToggle(c)}>
                        {c.enabled ? (
                          <>
                            <EyeOff /> Disable
                          </>
                        ) : (
                          <>
                            <Eye /> Enable
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => void onDelete(c)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

